/**
 * Keeps the horizontal filter bar's overflow list honest after a filter is
 * picked. Horizon's <overflow-list> measures its items once, then re-measures
 * only when its own box resizes or a direct child is added or removed. Picking
 * a value is neither: the section morph rewrites the chip's label in place
 * ("Size" becomes "Size 5ft King Size"), the chip grows, and the chips that no
 * longer fit wrap onto a second row the bar does not show. Watching the
 * chips themselves, and the text inside them, and asking the list to reflow
 * puts those chips back into its "More" overflow.
 */
const LIST_SELECTOR = '.facets--horizontal overflow-list';

/** @type {WeakSet<Element>} */
const watched = new WeakSet();

/**
 * @param {Element} list
 */
function watch(list) {
  if (watched.has(list)) return;
  watched.add(list);

  let scheduled = false;
  const reflow = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      list.dispatchEvent(new CustomEvent('reflow', { detail: {} }));
    });
  };

  const sizes = new ResizeObserver(reflow);
  const observeChips = () => {
    sizes.disconnect();
    for (const chip of list.children) {
      if (chip instanceof HTMLTemplateElement) continue;
      sizes.observe(chip);
    }
  };
  observeChips();

  // Label text changes on a filter morph; children change on a full re-render.
  new MutationObserver(() => {
    observeChips();
    reflow();
  }).observe(list, { childList: true, subtree: true, characterData: true });
}

function watchAll() {
  for (const list of document.querySelectorAll(LIST_SELECTOR)) watch(list);
}

watchAll();

// A collection re-render can replace the whole filter bar.
new MutationObserver((mutations) => {
  if (mutations.some((m) => m.addedNodes.length)) watchAll();
}).observe(document.body, { childList: true, subtree: true });
